/**
 * A page's due date (`080-calendar.md` §7) — "I need to prep this page before Tuesday".
 *
 * **It is not an event.** No category, no duration, no done state, no description. It is a
 * *page* that has a deadline, it is written with `PATCH /api/pages/{id} { dueAt, dueAllDay }`,
 * and clearing it sends `dueAt: null`. There is no shadow `event` row and there must never be
 * one — the moment there is, the calendar has two ways to say the same thing and they will
 * disagree.
 *
 * Two surfaces, one form: a popover on the page header (`080` §7) and a dialog the command
 * palette can open for the current page.
 */
import { Suspense, lazy, useState, useSyncExternalStore } from 'react';
import { format } from 'date-fns';
import { AlertCircle, CalendarClock } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { keys } from '@/api/keys';
import type { PageDto } from '@/api/endpoints/pages';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { currentDateLocale, useTranslation } from '@/lib/i18n';
import { dayKeyOf, formatTime, startOfDayKey } from '@/features/calendar/datetime';
import { closeDueDate, getDueDatePage, subscribeDueDate } from '@/features/calendar/dueDateStore';
import { useCalendarSettings } from '@/features/calendar/useCalendarSettings';

/**
 * LAZY — and this one is load-bearing for the bundle budget (`120` §2).
 *
 * `DueDateButton` sits in the page header, on the eager main route. The FORM behind it carries
 * `react-day-picker`; while the two lived in one module, every user who opened a page paid for a
 * calendar widget they never opened. The picker now loads when the popover does.
 */
const DueDateForm = lazy(async () => ({
  default: (await import('@/features/calendar/DueDateForm')).DueDateForm,
}));

/** The popover is 64px wide and the chunk is ~30 kB; a spinner here would be a flash. */
function FormFallback() {
  return <div className="h-40" aria-busy="true" />;
}

/** The page header's control (`080` §7, "Setting it"). */
export function DueDateButton({
  pageId,
  dueAt,
  dueAllDay,
}: {
  pageId: string;
  dueAt: string | null;
  dueAllDay: boolean;
}) {
  const { t } = useTranslation();
  const { timezone } = useCalendarSettings();
  const [open, setOpen] = useState(false);

  const overdue = dueAt !== null && new Date(dueAt).getTime() < Date.now();

  const label =
    dueAt === null
      ? t('calendar.due.setPrompt')
      : `${t('calendar.dueOn', {
          date: format(startOfDayKey(dayKeyOf(dueAt, timezone), timezone), 'EEE d MMM', {
            locale: currentDateLocale(),
          }),
        })}${dueAllDay ? '' : ` · ${formatTime(dueAt, timezone)}`}`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          data-testid="due-date-button"
          aria-label={label}
          title={label}
          className={cn(
            'gap-1 font-normal',
            dueAt === null && 'text-[var(--muted-foreground)]',
            overdue && 'text-[var(--warning)]',
          )}
        >
          {overdue ? <AlertCircle strokeWidth={1.75} /> : <CalendarClock strokeWidth={1.75} />}
          {dueAt === null ? t('calendar.due.buttonLabel') : label}
        </Button>
      </PopoverTrigger>

      <PopoverContent align="start" className="w-64">
        <Suspense fallback={<FormFallback />}>
          <DueDateForm
            pageId={pageId}
            dueAt={dueAt}
            dueAllDay={dueAllDay}
            onDone={() => {
              setOpen(false);
            }}
          />
        </Suspense>
      </PopoverContent>
    </Popover>
  );
}

/** The palette's "Set due date" command opens this, for the page you are on. */
export function DueDateDialogHost() {
  const { t } = useTranslation();
  const pageId = useSyncExternalStore(subscribeDueDate, getDueDatePage, () => null);
  const queryClient = useQueryClient();

  if (pageId === null) return null;

  // The page detail is already in the cache — this dialog is only ever opened from a page you
  // are looking at, so there is nothing to fetch.
  const page = queryClient.getQueryData<PageDto>(keys.pages.detail(pageId));

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) closeDueDate();
      }}
    >
      <DialogContent className="max-w-xs">
        <DialogHeader>
          <DialogTitle>{t('calendar.due.buttonLabel')}</DialogTitle>
        </DialogHeader>
        <Suspense fallback={<FormFallback />}>
          <DueDateForm
            pageId={pageId}
            dueAt={page?.dueAt ?? null}
            dueAllDay={page?.dueAllDay ?? true}
            onDone={closeDueDate}
          />
        </Suspense>
      </DialogContent>
    </Dialog>
  );
}
